/////////////////////////////////////////////////////////////////////////// main code 

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowLeft, Upload, CheckCircle, Info, X, FileText, Plus, BarChart3, Table, Layout, Eye, Save, ChevronRight, MoreVertical, CircleArrowLeft, CircleArrowRight, CircleArrowUp, CircleArrowDown, CircleX, EllipsisVertical, ChevronUp, ChevronDown } from 'lucide-react';
import { uploadTemplate, uploadChangeList, getAllTemplateManagementData } from '../../services/apiService';
import { decodedToken } from '../../utils/auth';

const TemplateManagement = () => {
  const navigate = useNavigate();
  const [templateData, setTemplateData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingCategory, setUploadingCategory] = useState(null);
  const [uploadingType, setUploadingType] = useState(null);
  const [uploadMessage, setUploadMessage] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [modalType, setModalType] = useState('error');
  const [duplicateInfo, setDuplicateInfo] = useState(null);
  const [activeTab, setActiveTab] = useState('CHANGE_VERIFICATION_ASSESSMENT');
  const [selectedCVAItem, setSelectedCVAItem] = useState(null);
  const [selectedCertificationItem, setSelectedCertificationItem] = useState(null);
  const [selectedPRItem, setSelectedPRItem] = useState(null);
  const [selectedPCItem, setSelectedPCItem] = useState(null);
  const [showHardwareModal, setShowHardwareModal] = useState(false);
  const [showAddWidgetModal, setShowAddWidgetModal] = useState(false);
  const [chartError, setChartError] = useState("");
  const [tableError, setTableError] = useState("");
  const [activeWidgetTab, setActiveWidgetTab] = useState('Chart'); // 'Chart' or 'Table and Matrix'
  const [showSettings, setShowSettings] = useState(false);
  const [showDropdownOptions, setShowDropdownOptions] = useState(false);
  const [activeSettingsSection, setActiveSettingsSection] = useState(null);
  const [activeSectionForSettings, setActiveSectionForSettings] = useState(null);
  const [showFullSettings, setShowFullSettings] = useState(null);
  const [showSettingsButton, setShowSettingsButton] = useState(false);
  const [activeSettingsButton, setActiveSettingsButton] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [reportLayout, setReportLayout] = useState({
    name: "",
    templateType: "",
    category: "",
    subCategory: "",
    productType: "",
    isDefault: false,
    status: "draft",
    createdBy:
      decodedToken()?.userType ?? "",

    updatedBy:
      decodedToken()?.userType ?? "",

    publishedBy:
      decodedToken()?.userType ?? "",

    sections: []
  });
  const [selectedTemplate, setSelectedTemplate] = useState("default-template");
  const [selectedSection, setSelectedSection] = useState("");

  const [sectionsOrder, setSectionsOrder] = useState([
    "header",
    "highlight",
    "chart",
    "table",
    "mandatory"
  ]);

  const [selectedWidgets, setSelectedWidgets] = useState({
    highlight: [],
    chart: [],
    table: [],
    mandatory: []
  });
  const [chartWidgetsOrder, setChartWidgetsOrder] = useState([
    "left",
    "right"
  ]);

  const [tableWidgetsOrder, setTableWidgetsOrder] = useState([
    "left",
    "right"
  ]);

  const [chartCards, setChartCards] = useState([
    {
      id: 1,
      image: "/chart1.png",
      side: "left",
      widget:
        selectedWidgets?.chart?.[0] || {
          widgetName: "TBA"
        }
    },

    {
      id: 2,
      image: "/chart2.png",
      side: "right",
      widget:
        selectedWidgets?.chart?.[1] || {
          widgetName: "TBD"
        }
    }
  ]);

  const [tableCards, setTableCards] = useState([
    {
      id: 1,
      image: "/table1.png",
      side: "left",
      widget:
        selectedWidgets?.table?.[0] || {
          widgetName: "TBA"
        }
    },

    {
      id: 2,
      image: "/table2.png",
      side: "right",
      widget:
        selectedWidgets?.table?.[1] || {
          widgetName: "TBD"
        }
    }
  ]);

  const swapChartWidgets = () => {
    setChartCards((prev) => [
      prev[1],
      prev[0]
    ]);
  };


  /* ===================== SWAP FUNCTION ===================== */

  const swapTableWidgets = () => {
    setTableCards((prev) => [
      prev[1],
      prev[0]
    ]);
  };
  // ============================
  // SECTION STATES
  // ============================

  const moveSectionUp = (sectionKey) => {
    setSectionsOrder((prev) => {
      const currentIndex =
        prev.indexOf(sectionKey);
      if (currentIndex === 0)
        return prev;
      const updated = [...prev];
      [
        updated[currentIndex - 1],
        updated[currentIndex]
      ] = [
          updated[currentIndex],
          updated[currentIndex - 1]
        ];

      return updated;
    });
  };

  const moveSectionDown = (sectionKey) => {
    setSectionsOrder((prev) => {
      const currentIndex =
        prev.indexOf(sectionKey);
      if (
        currentIndex === prev.length - 1
      )
        return prev;
      const updated = [...prev];
      [
        updated[currentIndex + 1],
        updated[currentIndex]
      ] = [
          updated[currentIndex],
          updated[currentIndex + 1]
        ];
      return updated;
    });
  };



  // Tab configuration
  const tabs = [
    { id: 'CHANGE_VERIFICATION_ASSESSMENT', label: 'Change Verification Assessment' },
    // { id: 'CERTIFICATION', label: 'Certification' },
    { id: 'PROJECT_REPORTING_UI_LAYOUT', label: 'Project Reporting UI Layout' },
    { id: 'VENDOR_CAPABILITY', label: 'Vendor Capability' },
    { id: 'PROJECT_CONTROL', label: 'Project Control' }
  ];

  // const changeverificationassessment = ['Hardware'];
  const changeverificationassessment = ['Hardware', 'Software'];
  const ProjectReportingUILayout = ['Hardware', 'Software', 'Certification'];
  const ProjectControl = ['Cert and SW Project Template', 'Cert_MNT Control Table', 'Cert_AVA Control Table']

  // Updated categories
  const categories = [
    { id: 'TV', label: 'TV' },
    { id: 'MNT', label: 'MNT' },
    { id: 'PD', label: 'PD' },
    { id: 'AVA', label: 'AVA' },
    { id: 'PD_Signage & IWB', label: 'PD_Signage & IWB' },
    { id: 'PD_RGB LED', label: 'PD_RGB LED' },
    { id: 'PD_AIO', label: 'PD_AIO' },
    { id: 'PD_EPD', label: 'PD_EPD' },
    { id: 'AVA_Audio', label: 'AVA_Audio' },
    { id: 'AVA_Headphone', label: 'AVA_Headphone' }
  ];

  // Certification sub-items
  const certificationItems = [
    'TV Daily Report Project Management',
    'TV Daily Report UI Layout Management',
    'MNT Control Table',
    'AVA Control Table'
  ];

  const VendorCapability = ['Hardware', 'Software', 'Certification'];

  const vendorcategories = [
    { id: 'TV', label: 'TV' },
    { id: 'MNT', label: 'MNT' },
    { id: 'PD', label: 'PD' },
    { id: 'AVA', label: 'AVA' },
    { id: 'PD_Signage & IWB', label: 'PD_Signage & IWB' },
    { id: 'PD_RGB LED', label: 'PD_RGB LED' },
    { id: 'PD_AIO', label: 'PD_AIO' },
    { id: 'PD_EPD', label: 'PD_EPD' },
    { id: 'AVA_Audio', label: 'AVA_Audio' },
    { id: 'AVA_Headphone', label: 'AVA_Headphone' }
  ];

  const selectedType = selectedCVAItem?.toLowerCase();
  const isHardware = selectedType === 'hardware';
  const isSoftware = selectedType === 'software';
  const isCertification = selectedType === 'certification';

  const vendorTitle = isHardware
    ? 'Hardware Vendor Capability Template'
    : isSoftware
      ? 'Software Vendor Capability Template'
      : 'Certification Vendor Capability Template';

  const assessmentTitle = isHardware
    ? 'HW Change Assessment Reference Table'
    : 'SW Change Assessment Reference Table';

  const getCertificationTitle = (item) => {
    if (item === 'MNT Control Table' || item === 'AVA Control Table') {
      return `${item} Template`;
    }
    return item;
  };

  // ─── Data Loading ───────────────────────────────────────────────────────────

  const loadAllData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getAllTemplateManagementData();
      if (response.success) {
        setTemplateData(response.data);
        console.log('✓ Template management data loaded successfully');
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setUploadError('Failed to load template data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setActiveSettingsButton(null);
      setActiveSectionForSettings(null);
    };

    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);


  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      if (isMounted) await loadAllData();
    };
    fetchData();
    return () => { isMounted = false; };
  }, [loadAllData]);

  useEffect(() => {
    if (activeTab === 'CHANGE_VERIFICATION_ASSESSMENT') {
      if (!selectedCVAItem) setSelectedCVAItem('Hardware');
    } else if (activeTab === 'CERTIFICATION') {
      if (!selectedCertificationItem) setSelectedCertificationItem(certificationItems[0]);
    } else if (activeTab === 'VENDOR_CAPABILITY') {
      if (!selectedCVAItem) setSelectedCVAItem('Hardware');
    }
  }, [activeTab]);


  const getWidgetImage = (sectionKey, position = 0) => {
    const imageMap = {

      highlight: [
        "/highlight.png"
      ],

      chart: [
        "/chart1.png",
        "/chart2.png"
      ],

      table: [
        "/table1.png",
        "/table2.png"
      ],

      mandatory: [
        "/highlight.png"
      ]
    };

    return imageMap?.[sectionKey]?.[position] || null;
  };


  // Dynamic Report Layout Payload
  // ============================
  // DYNAMIC PAYLOAD
  // ============================

  useEffect(() => {

    if (selectedPRItem) {

      let templateName = "Default template";
      let templateType = "default";
      let isDefaultValue = true;

      if (selectedTemplate === "outsourcing-weekly-report") {

        templateName = "Outsourcing Weekly Report";
        templateType = "outsourcing";
        isDefaultValue = false;

      } else if (selectedTemplate === "aic-weekly-highlights") {

        templateName = "AIC Weekly Highlights";
        templateType = "aic";
        isDefaultValue = false;
      }

      setReportLayout({
        name: templateName,
        templateType: templateType,
        category: "Project Reporting UI Layout",
        subCategory: selectedPRItem,
        productType: null,
        isDefault: isDefaultValue,
        status: "draft",
        // ================= USER INFO =================

        createdBy:
          decodedToken()?.userType ?? "",

        updatedBy:
          decodedToken()?.userType ?? "",

        publishedBy:
          decodedToken()?.userType ?? "",

        // ================= DYNAMIC SECTIONS =================

        sections:
          sectionsOrder.map((sectionKey, sectionIndex) => {

            // ================= HEADER =================

            if (sectionKey === "header") {
              return {
                sectionKey: "header",
                sectionName: "Header",
                position: sectionIndex,
                editable: false,
                rules: {
                  maxWidgets: 0,
                  allowedCategories: [],
                  allowDuplicates: false,
                  mandatory: true
                },

                widgets: []
              };
            }
            // ================= HIGHLIGHT =================

            if (sectionKey === "highlight") {
              return {
                sectionKey: "highlight",
                sectionName: "Highlight",
                position: sectionIndex,
                editable: true,
                rules: {
                  maxWidgets: 1,
                  allowedCategories: ["highlight"],
                  allowDuplicates: false,
                  mandatory: false
                },

                widgets:
                  selectedWidgets?.highlight?.length > 0
                    ? selectedWidgets.highlight.map((widget, index) => ({
                      widgetKey:
                        widget?.widgetKey,
                      widgetName:
                        widget?.widgetName,
                      category: "highlight",
                      position: index,
                      side: "full",
                      subIndex: 0,
                      locked: false,
                      config: {
                        image:
                          getWidgetImage("highlight", index)
                      }
                    }))
                    : []
              };
            }
            // ================= CHART =================

            if (sectionKey === "chart") {
              return {
                sectionKey: "chart",
                sectionName: "Chart",
                position: sectionIndex,
                editable: true,
                rules: {
                  maxWidgets: 2,
                  allowedCategories: ["chart"],
                  allowDuplicates: false,
                  mandatory: false
                },

                widgets:
                  selectedWidgets?.chart?.length > 0
                    ? selectedWidgets.chart.map((widget, index) => ({
                      widgetKey:
                        widget?.widgetKey,
                      widgetName:
                        widget?.widgetName,
                      category: "chart",
                      position: index,
                      side:
                        chartWidgetsOrder[index],
                      subIndex: 0,
                      locked: false,
                      config: {
                        image:
                          getWidgetImage("chart", index)
                      }
                    }))
                    : []
              };
            }
            // ================= TABLE =================

            if (sectionKey === "table") {
              return {
                sectionKey: "table",
                sectionName: "Table",
                position: sectionIndex,
                editable: true,
                rules: {
                  maxWidgets: 2,
                  allowedCategories: ["table_matrix"],
                  allowDuplicates: false,
                  mandatory: false
                },

                widgets:
                  selectedWidgets?.table?.length > 0
                    ? selectedWidgets.table.map((widget, index) => ({
                      widgetKey:
                        widget?.widgetKey,
                      widgetName:
                        widget?.widgetName,
                      category: "table_matrix",
                      position: index,
                      side:
                        tableWidgetsOrder[index],
                      subIndex: 0,
                      locked: false,
                      config: {
                        image:
                          getWidgetImage("table", index)
                      }
                    }))
                    : []
              };
            }
            // ================= MANDATORY =================

            if (sectionKey === "mandatory") {
              return {
                sectionKey: "mandatory",
                sectionName: "Mandatory Section",
                position: sectionIndex,
                editable: false,
                rules: {
                  maxWidgets: 1,
                  allowedCategories: ["table_matrix"],
                  allowDuplicates: false,
                  mandatory: true
                },
                widgets:
                  selectedWidgets?.mandatory?.length > 0
                    ? selectedWidgets.mandatory.map((widget, index) => ({
                      widgetKey:
                        widget?.widgetKey,
                      widgetName:
                        widget?.widgetName,
                      category: "table_matrix",
                      position: index,
                      side: "full",
                      subIndex: 0,
                      locked: true,
                      config: {
                        image:
                          getWidgetImage("mandatory", index)
                      }
                    }))

                    : []
              };
            }
            return null;
          }).filter(Boolean)
      });
    }
  }, [
    selectedPRItem,
    selectedTemplate,
    selectedWidgets,
    sectionsOrder,
    chartWidgetsOrder,
    tableWidgetsOrder,
    decodedToken()?.userType
  ]);

  // ─── Data Helpers ────────────────────────────────────────────────────────────
  ////////////////////////////////////////////////software

  const getDataForCategory = (productTypeId) => {
    const categoryMap = {
      CHANGE_VERIFICATION_ASSESSMENT: 'Change Verification Assessment',
      VENDOR_CAPABILITY: 'Vendor Capability'
    };

    const category = categoryMap[activeTab];

    // ONLY CHANGE (software normalization)
    const sub_category =
      selectedCVAItem === 'Software'
        ? 'software'
        : selectedCVAItem;

    const found = templateData.find(item =>
      item.category === category &&
      item.sub_category?.toLowerCase() === sub_category?.toLowerCase() &&
      item.productType === productTypeId
    );

    return found || {
      category,
      sub_category,
      productType: productTypeId,
      referenceTable: null,
      changeList: null
    };
  };

  // For Certification tab — productType is null, matched by sub_category
  const getDataForCertification = (subCatId) => {
    const found = templateData.find(item =>
      item.category === 'Certification' &&
      item.sub_category?.toLowerCase() === subCatId?.toLowerCase() &&
      item.productType === null
    );
    return found || {
      category: 'Certification',
      sub_category: subCatId,
      productType: null,
      referenceTable: null,
      changeList: null
    };
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // ─── Upload Handlers ─────────────────────────────────────────────────────────

  const handleFileUpload = async (event, productTypeId, type = 'reference') => {
    const file = event.target.files[0];
    if (!file) return;

    const validExtensions = ['.xlsx', '.xls', '.xlsm'];
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

    if (!validExtensions.includes(fileExtension)) {
      setModalType('error');
      setModalMessage('Please upload an Excel file (.xlsx, .xls, or .xlsm)');
      setShowModal(true);
      event.target.value = '';
      return;
    }
    const isCertTab = activeTab === 'CERTIFICATION';

    try {
      const uploadKey = isCertTab ? selectedCertificationItem : productTypeId;
      setUploadingCategory(uploadKey);
      setUploadingType(type);
      setUploadError('');
      setUploadMessage('');

      const decoded = decodedToken();
      const email = decoded?.email || '';

      if (!email) {
        setModalType('error');
        setModalMessage('Unable to retrieve user email. Please log in again.');
        setShowModal(true);
        event.target.value = '';
        setUploadingCategory(null);
        setUploadingType(null);
        return;
      }

      const categoryMap = {
        CHANGE_VERIFICATION_ASSESSMENT: 'Change Verification Assessment',
        CERTIFICATION: 'Certification',
        VENDOR_CAPABILITY: 'Vendor Capability'
      };
      const category = categoryMap[activeTab];

      let sub_category;
      let productType;

      if (isCertTab) {
        sub_category = selectedCertificationItem;
        productType = null;
      } else {
        sub_category = selectedCVAItem;
        productType = productTypeId;
      }

      console.log(`Uploading → category: ${category} | sub_category: ${sub_category} | productType: ${productType ?? 'null'} | file: ${file.name}`);

      let softwareType = null;

      if (
        category === 'Change Verification Assessment' &&
        sub_category?.trim().toLowerCase() === 'software'
      ) {
        softwareType = type === 'changelist' ? 'changelist' : 'reference';
      }

      console.log("softwareType:", softwareType);
      console.log("FINAL softwareType before API:", softwareType);

      const response = await uploadTemplate(
        file,
        category,
        sub_category,
        productType,
        email,
        softwareType
      );

      if (response.success) {
        if (response.isDuplicate) {
          setModalType('duplicate');
          setDuplicateInfo(response.existingTemplate);
          const label = isCertTab ? sub_category : productTypeId;
          setModalMessage(
            `The uploaded template is identical to the existing ${label} template.\n\n` +
            `No changes detected in:\n` +
            `• Test case mappings\n` +
            `• Test categories\n` +
            `• Change item definitions\n\n` +
            `The existing template will continue to be used.`
          );
          setShowModal(true);
          setUploadMessage(`Template data unchanged. Current version remains active.`);
          setTimeout(() => setUploadMessage(''), 5000);
        } else {
          setModalType('success');
          setModalMessage(`Template uploaded successfully!`);
          setShowModal(true);
          const label = isCertTab ? sub_category : productTypeId;
          setUploadMessage(`Template uploaded successfully for ${label}`);
          setTimeout(() => setUploadMessage(''), 5000);
        }
        await loadAllData();
      } else {
        setModalType('error');
        setModalMessage(response.message || 'Upload failed');
        setShowModal(true);
        setUploadError(response.message || 'Upload failed');
        setTimeout(() => setUploadError(''), 5000);
      }
    } catch (error) {
      console.error('Upload error:', error);
      let errorMessage = 'Unknown error';
      if (error.response?.data) {
        if (error.response.data.success === false) {
          errorMessage = error.response.data.message || error.response.data.error;
        } else if (error.response.data.error === 'Invalid template file') {
          errorMessage = error.response.data.message;
          if (error.response.data.availableSheets) {
            errorMessage += `\n\nAvailable sheets in the file:\n${error.response.data.availableSheets.join(', ')}`;
          }
        } else {
          errorMessage = error.response.data.message || error.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      setModalType('error');
      setModalMessage(`Failed to upload template:\n\n${errorMessage}`);
      setShowModal(true);
      setUploadError(`Upload failed: ${errorMessage}`);
      setTimeout(() => setUploadError(''), 5000);
    } finally {
      setUploadingCategory(null);
      setUploadingType(null);
      event.target.value = '';
    }
  };

  const handleChangeListUpload = async (event, productType) => {
    const file = event.target.files[0];
    if (!file) return;

    const validExtensions = ['.xlsx', '.xls', '.xlsm'];
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

    if (!validExtensions.includes(fileExtension)) {
      setModalType('error');
      setModalMessage('Please upload an Excel file (.xlsx, .xls, or .xlsm)');
      setShowModal(true);
      event.target.value = '';
      return;
    }

    try {
      setUploadingCategory(productType);
      setUploadingType('changelist');
      setUploadError('');
      setUploadMessage('');

      const decoded = decodedToken();
      const email = decoded?.email || '';
      const userType = decoded?.userType || 'PowerAdmin';

      if (!email) {
        setModalType('error');
        setModalMessage('Unable to retrieve user email. Please log in again.');
        setShowModal(true);
        event.target.value = '';
        setUploadingCategory(null);
        setUploadingType(null);
        return;
      }

      if (!userType) {
        setModalType('error');
        setModalMessage('Unable to retrieve user type. Please log in again.');
        setShowModal(true);
        event.target.value = '';
        setUploadingCategory(null);
        setUploadingType(null);
        return;
      }

      const categoryMap = {
        CHANGE_VERIFICATION_ASSESSMENT: 'Change Verification Assessment',
        CERTIFICATION: 'Certification',
        VENDOR_CAPABILITY: 'Vendor Capability'
      };
      const category = categoryMap[activeTab];
      const sub_category = selectedCVAItem;

      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', category);
      formData.append('sub_category', sub_category);
      formData.append('productType', productType);

      const response = await uploadChangeList(formData, email, userType);

      if (response.success) {
        if (response.isDuplicate) {
          setModalType('duplicate');
          setDuplicateInfo(response.existingChangeList);
          setModalMessage(
            `The uploaded change list is identical to the existing ${productType} template.\n\n` +
            `No changes detected.\n\n` +
            `The existing template will continue to be used.`
          );
          setShowModal(true);
          setUploadMessage(`Change list unchanged. Current version remains active.`);
          setTimeout(() => setUploadMessage(''), 5000);
        } else {
          setModalType('success');
          setModalMessage(`Change list uploaded successfully!`);
          setShowModal(true);
          setUploadMessage(`Change list uploaded successfully for ${productType}`);
          setTimeout(() => setUploadMessage(''), 5000);
        }
        await loadAllData();
      }
    } catch (error) {
      console.error('Upload error:', error);
      let errorMessage = error.message || 'Unknown error';
      if (error.response?.data?.error === 'Invalid Excel file') {
        errorMessage = error.response.data.message;
        if (error.response.data.expectedSheets) {
          errorMessage += `\n\nExpected sheets: ${error.response.data.expectedSheets.join(', ')}`;
        }
      }
      setModalType('error');
      setModalMessage(`Failed to upload change list:\n\n${errorMessage}`);
      setShowModal(true);
      setUploadError(`Upload failed: ${errorMessage}`);
      setTimeout(() => setUploadError(''), 5000);
    } finally {
      setUploadingCategory(null);
      setUploadingType(null);
      event.target.value = '';
    }
  };

  // ─── Misc Handlers ───────────────────────────────────────────────────────────

  const handleGoBack = () => navigate('/management');

  const closeModal = () => {
    setShowModal(false);
    setModalMessage('');
    setDuplicateInfo(null);
  };

  const getModalStyles = () => {
    switch (modalType) {
      case 'duplicate':
        return {
          bgColor: 'bg-yellow-900/30',
          borderColor: 'border-yellow-500',
          iconColor: 'text-yellow-400',
          icon: Info,
          title: 'Duplicate Template Detected'
        };
      case 'success':
        return {
          bgColor: 'bg-green-900/30',
          borderColor: 'border-green-500',
          iconColor: 'text-green-400',
          icon: CheckCircle,
          title: 'Success'
        };
      default:
        return {
          bgColor: 'bg-red-900/30',
          borderColor: 'border-red-500',
          iconColor: 'text-red-400',
          icon: AlertCircle,
          title: 'Upload Error'
        };
    }
  };

  // ─── Loading ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0E1114] text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading templates...</p>
        </div>
      </div>
    );
  }

  const modalStyles = getModalStyles();
  const ModalIcon = modalStyles.icon;

  const handleSubmitLayout = async (submitStatus = "draft") => {
    try {
      const finalPayload = {
        ...reportLayout,
        status: submitStatus,
        sections: reportLayout.sections.map((section, index) => ({
          ...section,
          position: index
        }))
      };

      console.log("Final Payload being sent:", finalPayload);

      const response = await fetch('https://panelai.tpvai.com/b/api/report-layout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // Add your authorization header if needed
          // Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(finalPayload),
      });

      const result = await response.json();

      if (response.ok) {
        alert(`Template ${submitStatus === 'draft' ? 'Saved as Draft' : 'Published'} Successfully!`);
      } else {
        alert(`Failed: ${result.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error(error);
      alert("Error while submitting template");
    }
  };


  // ====================== HELPER FUNCTIONS ======================

  const addWidget = (sectionKey, widgetData) => {
    setReportLayout(prev => ({
      ...prev,
      sections: prev.sections.map(section => {
        if (section.sectionKey === sectionKey) {
          return {
            ...section,
            widgets: [
              ...section.widgets,
              {
                ...widgetData,
                position: section.widgets.length,
                side: section.widgets.length === 0 ? "left" : "right",
                subIndex: 0,
                locked: false,
                config: {}
              }
            ]
          };
        }
        return section;
      })
    }));
  };

  const removeWidget = (sectionKey, widgetKey) => {
    setReportLayout(prev => ({
      ...prev,
      sections: prev.sections.map(section =>
        section.sectionKey === sectionKey
          ? {
            ...section,
            widgets: section.widgets.filter(w => w.widgetKey !== widgetKey)
          }
          : section
      )
    }));
  };

  const updateTemplateField = (field, value) => {
    setReportLayout(prev => ({ ...prev, [field]: value }));
  };

  const updateSectionName = (sectionKey, newName) => {
    setReportLayout(prev => ({
      ...prev,
      sections: prev.sections.map(section =>
        section.sectionKey === sectionKey ? { ...section, sectionName: newName } : section
      )
    }));
  };

  // ============================
  // WIDGET OPTIONS
  // ADD ABOVE return (
  // ============================

  const chartWidgets = [

    {
      widgetKey: "certified_logo_pie_chart",
      widgetName:
        "Certified and Ongoing Logo Pie Chart"
    },

    {
      widgetKey: "daily_open_pr_stackbar_chart",
      widgetName:
        "Daily Total Open PR v.s Status Stackbar Chart"
    },

    {
      widgetKey: "gating_cs_pr_solving_trend",
      widgetName:
        "Gating CS PR Solving Trend"
    },

    {
      widgetKey: "test_pass_fail_rate_bar_chart",
      widgetName:
        "Test Pass/Fail Rate Bar Chart"
    }
  ];


  const tableWidgets = [

    {
      widgetKey: "cert_daily_highlight_issue_table",
      widgetName:
        "Cert. Daily Highlight Issue Table"
    },

    {
      widgetKey: "cert_issue_severity_state_matrix_table",
      widgetName:
        "Cert Issue Severity & State Matrix Table"
    },

    {
      widgetKey: "logo_detail_status_matrix_table",
      widgetName:
        "Logo Detail Status Matrix Table"
    }
  ];

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-[#0E1114] text-white">
      {/* Header */}
      <div className="border-b border-gray-800">
        <div className="px-6 py-4 flex items-center gap-3">
          <ArrowLeft
            className="w-6 h-6 cursor-pointer hover:text-blue-400 transition-colors"
            onClick={handleGoBack}
          />
          <h1 className="text-xl font-semibold">Template Management</h1>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-t border-gray-800">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 px-2 sm:px-6 py-2 sm:py-3 text-sm sm:text-base font-medium transition-colors relative ${activeTab === tab.id
                ? 'text-white bg-gray-900'
                : 'text-gray-400 hover:text-white hover:bg-gray-900/50'
                }`}
            >
              {tab.label}
              {activeTab === tab.id && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500"></div>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="p-6">
        {/* Status Messages */}
        {uploadMessage && (
          <div className="mb-4 p-3 bg-green-900/50 border border-green-500 rounded-lg flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
            <p className="text-green-300 text-sm">{uploadMessage}</p>
          </div>
        )}
        {uploadError && (
          <div className="mb-4 p-3 bg-red-900/50 border border-red-500 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
            <p className="text-red-300 text-sm">{uploadError}</p>
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className={`${modalStyles.bgColor} border ${modalStyles.borderColor} p-6 rounded-lg max-w-md w-full shadow-2xl`}>
              <div className="flex items-center gap-3 mb-4">
                <ModalIcon className={`w-8 h-8 ${modalStyles.iconColor} flex-shrink-0`} />
                <h3 className="text-lg font-semibold text-white">{modalStyles.title}</h3>
              </div>
              <div className="mb-6">
                <p className="text-sm text-gray-300 whitespace-pre-line leading-relaxed">{modalMessage}</p>
                {duplicateInfo && (
                  <div className="mt-4 p-3 bg-gray-800/50 rounded border border-gray-700">
                    <p className="text-xs text-gray-400 mb-1">
                      <span className="font-semibold">Filename:</span> {duplicateInfo.filename}
                    </p>
                    <p className="text-xs text-gray-400">
                      <span className="font-semibold">Last Updated:</span> {formatDate(duplicateInfo.uploadDate)}
                    </p>
                  </div>
                )}
              </div>
              <div className="flex justify-end">
                <button
                  className={`px-6 py-2 rounded-md font-medium transition-all ${modalType === 'duplicate' ? 'bg-yellow-600 hover:bg-yellow-500' :
                    modalType === 'success' ? 'bg-green-600 hover:bg-green-500' :
                      'bg-blue-600 hover:bg-blue-500'
                    } text-white active:scale-95`}
                  onClick={closeModal}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}

        {/* change verification assessment */}

        {activeTab === 'CHANGE_VERIFICATION_ASSESSMENT' && (
          <div className="sm:mt-6 mt-1 space-y-6">

            {/* TOP MENU */}
            <div className="w-full max-w-lg">
              <div className="bg-[#1a1d23] rounded-lg border border-gray-800 divide-y divide-gray-800">
                {changeverificationassessment.map((item, index) => (
                  <div
                    key={index}
                    onClick={() => setSelectedCVAItem(prev => (prev === item ? null : item))}
                    className={`px-4 py-3 cursor-pointer transition-colors ${selectedCVAItem === item ? 'bg-gray-600 text-white' : 'hover:bg-gray-700'
                      }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-white" />
                      <span className="text-base">{item}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* DETAILS — Dynamic for Hardware & Software */}
            {selectedCVAItem && (
              <div className="space-y-6 w-full">

                {/* HEADER */}
                <div className="px-4 sm:px-6 md:px-10">
                  <h2 className="text-base sm:text-2xl md:text-3xl font-semibold text-white mb-4">
                    {selectedCVAItem} Change Verification Assessment Template
                  </h2>

                  <div className="p-3 bg-yellow-900/20 border border-yellow-600/30 rounded-lg flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs sm:text-sm md:text-base text-yellow-200 leading-relaxed">
                      <span className="font-semibold">Attention:</span> Before update, please first rename
                      the Excel file as per the defined naming convention.
                    </p>
                  </div>
                </div>

                {/* category loop */}
                {categories.map((category) => {
                  const data = getDataForCategory(category.id);
                  const template = data.referenceTable;
                  const changeList = data.changeList;

                  const isUploadingReference =
                    uploadingCategory === category.id && uploadingType === 'reference';

                  const isUploadingChangeList =
                    uploadingCategory === category.id && uploadingType === 'changelist';

                  //  NEW LOGIC (Software condition handling)
                  // const isSoftware = selectedCVAItem === 'Software';
                  const isSoftware =
                    selectedCVAItem?.toLowerCase().trim() === 'software';

                  const showReferenceOnly =
                    isSoftware && ['PD', 'AVA'].includes(category.id);

                  const showChangeListOnly =
                    isSoftware &&
                    [
                      'PD_Signage & IWB',
                      'PD_RGB LED',
                      'PD_AIO',
                      'PD_EPD',
                      'AVA_Audio',
                      'AVA_Headphone'
                    ].includes(category.id);

                  const showBoth =
                    !isSoftware || (!showReferenceOnly && !showChangeListOnly);

                  return (
                    <div key={category.id} className="space-y-4 w-full">

                      <h2 className="text-lg font-semibold text-blue-500">
                        {category.label}
                      </h2>

                      {/* Reference Table */}
                      {(showReferenceOnly || showBoth) && (
                        <>
                          <h3 className="text-sm font-medium text-gray-300">
                            {selectedCVAItem === 'Hardware' ? 'HW' : 'SW'} Change Assessment Reference Table
                          </h3>

                          <div className="bg-[#1a1d23] rounded-lg p-4 border border-gray-800 hover:border-gray-700 transition-colors w-full">
                            {template ? (
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-white mb-2">
                                    {template.filename}
                                  </p>
                                  <p className="text-xs text-gray-400 mb-1">
                                    Uploaded by: {template.uploadedBy}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    Last updated: {formatDate(template.lastModified || template.uploadDate)}
                                  </p>
                                  {template.fileVersion && (
                                    <p className="text-xs text-gray-500 font-mono mt-1">
                                      Version: {template.fileVersion}
                                    </p>
                                  )}
                                </div>
                                <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 ml-4" />
                              </div>
                            ) : (
                              <div className="text-center py-4">
                                <AlertCircle className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                                <p className="text-sm text-gray-500">
                                  No template uploaded for {category.label}
                                </p>
                              </div>
                            )}
                          </div>

                          <div className="flex justify-end">
                            <label
                              className={`bg-gray-700 hover:bg-gray-600 text-sm text-white px-5 py-2.5 rounded-md transition-all cursor-pointer inline-flex items-center gap-2 ${isUploadingReference ? 'opacity-50 cursor-not-allowed' : 'active:scale-95'
                                }`}
                            >
                              {isUploadingReference ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                                  Uploading...
                                </>
                              ) : (
                                <>
                                  <Upload className="w-4 h-4" />
                                  Update Template
                                </>
                              )}

                              <input
                                type="file"
                                className="hidden"
                                accept=".xlsx,.xls,.xlsm"
                                onChange={(e) => handleFileUpload(e, category.id)}
                                disabled={isUploadingReference}
                              />
                            </label>
                          </div>
                        </>
                      )}

                      {/*  Change Item List */}
                      {(showChangeListOnly || showBoth) && (
                        <>
                          <h3 className="text-sm font-medium text-gray-300">
                            {selectedCVAItem === 'Hardware' ? 'HW' : 'SW'} Change Item List
                          </h3>

                          <div className="bg-[#1a1d23] rounded-lg p-4 border border-gray-800 hover:border-gray-700 transition-colors w-full">
                            {changeList ? (
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-white mb-2">
                                    {changeList.filename}
                                  </p>
                                  <p className="text-xs text-gray-400 mb-1">
                                    Uploaded by: {changeList.uploadedBy}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    Last updated: {formatDate(changeList.uploadDate)}
                                  </p>
                                </div>
                                <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 ml-4" />
                              </div>
                            ) : (
                              <div className="text-center py-4">
                                <AlertCircle className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                                <p className="text-sm text-gray-500">
                                  No change list uploaded for {category.label}
                                </p>
                              </div>
                            )}
                          </div>

                          <div className="flex justify-end">
                            <label
                              className={`bg-gray-700 hover:bg-gray-600 text-sm text-white px-5 py-2.5 rounded-md transition-all cursor-pointer inline-flex items-center gap-2 ${isUploadingChangeList ? 'opacity-50 cursor-not-allowed' : 'active:scale-95'
                                }`}
                            >
                              {isUploadingChangeList ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                                  Uploading...
                                </>
                              ) : (
                                <>
                                  <Upload className="w-4 h-4" />
                                  Update Change List
                                </>
                              )}

                              <input
                                type="file"
                                className="hidden"
                                accept=".xlsx,.xls,.xlsm"
                                onChange={(e) =>
                                  isSoftware
                                    ? handleFileUpload(e, category.id, 'changelist')
                                    : handleChangeListUpload(e, category.id)
                                }
                                disabled={isUploadingChangeList}
                              />
                            </label>
                          </div>
                        </>
                      )}

                      <div className="border-t border-gray-800 my-6"></div>
                    </div>
                  );
                })}

                {/* Upload Guidelines */}
                <div className="mt-8 p-4 bg-blue-900/10 border border-blue-500/20 rounded-lg w-full">
                  <h3 className="text-sm font-semibold text-blue-400 mb-3">
                    Template Upload Guidelines
                  </h3>
                  <ul className="text-xs text-gray-400 space-y-1.5">
                    <li>• Upload Excel files (.xlsx, .xls, .xlsm)</li>
                    <li>• Follow the predefined template format</li>
                    <li>• New uploads replace the existing template</li>
                    <li>• Supported product types: {categories.map(c => c.label).join(', ')}</li>
                  </ul>
                </div>

                {/* Template Status */}
                <div className="mt-6 p-4 bg-gray-900/30 border border-gray-700/30 rounded-lg w-full">
                  <h3 className="text-sm font-semibold text-gray-300 mb-4">
                    Template Status
                  </h3>

                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    {categories.map((category) => {
                      const data = getDataForCategory(category.id);
                      const template = data.referenceTable || data.changeList;;

                      return (
                        <div key={category.id} className="flex items-center gap-2">
                          <div className={`text-lg ${template ? 'text-green-400' : 'text-gray-300'}`}>
                            {template ? '✓' : '○'}
                          </div>
                          <div>
                            <div className="text-xs text-white font-semibold">
                              {category.label}
                            </div>
                            {template?.fileVersion && (
                              <div className="text-xs text-gray-500 font-mono">
                                {template.fileVersion}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}


        {/* ══════════════════════════════════════════════════════════════════════
            TAB: VENDOR CAPABILITY
        ══════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'VENDOR_CAPABILITY' && (
          <div className="sm:mt-6 mt-1 space-y-6">
            <div className="w-full flex justify-center md:justify-end">
              <div className="w-full sm:w-80 md:w-96 lg:w-[520px] md:mr-32 lg:mr-120 ">
                <div className="bg-[#1a1d23] border border-gray-800 divide-y divide-gray-800 rounded-lg overflow-hidden">
                  {VendorCapability.map((item, index) => (
                    <div
                      key={index}
                      onClick={() => setSelectedCVAItem(prev => (prev === item ? null : item))}
                      className={`px-4 py-3 cursor-pointer transition-colors ${selectedCVAItem === item
                        ? 'bg-gray-600 text-white'
                        : 'hover:bg-gray-700'
                        }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-1.5 h-1.5 rounded-full bg-white" />
                        <span className="text-sm sm:text-base">{item}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {(isHardware || isSoftware || isCertification) && (
              <div className="mt-6 w-full space-y-6">
                {/* Static Header */}
                <div className="ml-4">
                  <h2 className="sm:text-3xl text-base font-semibold text-white mb-2 ml-3">
                    {vendorTitle}
                  </h2>
                  <div className="p-3 flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs sm:text-sm md:text-base text-yellow-200 leading-relaxed">
                      <span className="font-semibold">Attention:</span> Before update, please
                      first rename the Excel file as: Product type Cert Verification
                      Assessment_version.xlsm
                    </p>
                  </div>
                </div>

                {/* Category Loop */}
                {vendorcategories.map((category) => {
                  // Hardware & Software: hide main PD and AVA
                  if ((isHardware || isSoftware) && (category.id === 'PD' || category.id === 'AVA')) {
                    return null;
                  }
                  // Certification: only show TV, MNT, PD, AVA
                  if (isCertification && !['TV', 'MNT', 'PD', 'AVA'].includes(category.id)) {
                    return null;
                  }

                  const data = getDataForCategory(category.id);
                  const template = data.referenceTable || data.changeList;
                  const isUploadingReference =
                    uploadingCategory === category.id && uploadingType === 'reference';

                  return (
                    <div key={category.id} className="space-y-2 w-full">
                      <h2 className="text-lg font-semibold text-blue-500 flex items-center gap-2">
                        {isCertification && ['TV', 'MNT', 'PD', 'AVA'].includes(category.id) && (
                          <FileText className="w-5 h-5 text-blue-400" />
                        )}
                        {category.label}
                      </h2>

                      <h3 className="text-base font-medium mb-1 text-green-300">
                        {isHardware
                          ? 'HW Capability Map Reference Table'
                          : isSoftware
                            ? 'SW Capability Map Reference Table'
                            : ''}
                      </h3>

                      {/* Template Card */}
                      <div className="bg-[#1a1d23] rounded-lg p-4 mb-3 border border-gray-800 hover:border-gray-700 transition-colors w-full">
                        {template ? (
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <p className="text-sm font-medium text-white mb-2">{template.filename}</p>
                              <p className="text-xs text-gray-400 mb-1">Uploaded by: {template.uploadedBy}</p>
                              <p className="text-xs text-gray-500">
                                Last updated: {formatDate(template.lastModified || template.uploadDate)}
                              </p>
                              {template.fileVersion && (
                                <p className="text-xs text-gray-500 font-mono mt-1">Version: {template.fileVersion}</p>
                              )}
                            </div>
                            <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 ml-4" />
                          </div>
                        ) : (
                          <div className="text-center py-4">
                            <AlertCircle className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                            <p className="text-sm text-gray-500">No template uploaded for {category.label}</p>
                          </div>
                        )}
                      </div>

                      {/* Upload Button */}
                      <div className="flex justify-end">
                        <label className={`bg-gray-700 hover:bg-gray-600 text-sm text-white px-5 py-2.5 rounded-md transition-all cursor-pointer inline-flex items-center gap-2 ${isUploadingReference ? 'opacity-50 cursor-not-allowed' : 'active:scale-95'
                          }`}>
                          {isUploadingReference ? (
                            <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />Uploading...</>
                          ) : (
                            <><Upload className="w-4 h-4" />Update Template</>
                          )}
                          <input
                            type="file"
                            className="hidden"
                            accept=".xlsx,.xls,.xlsm"
                            onChange={(e) => handleFileUpload(e, category.id)}
                            disabled={isUploadingReference}
                          />
                        </label>
                      </div>
                      <div className="border-t border-gray-800 my-6" />
                    </div>
                  );
                })}

                {/* Upload Guidelines */}
                <div className="mt-8 p-4 bg-blue-900/10 border border-blue-500/20 rounded-lg">
                  <h3 className="text-base font-semibold text-blue-400 mb-3">Template Upload Guidelines</h3>
                  <ul className="text-sm text-white space-y-1.5 leading-relaxed">
                    <li>• Upload Excel files (.xlsx, .xls, or .xlsm)</li>
                    <li>• Each update file must follow the predefined capability format</li>
                    <li>• Uploading a new file will replace the existing one</li>
                    <li>• Supported category: {isHardware ? 'Hardware' : isSoftware ? 'Software' : 'Certification'}</li>
                  </ul>
                </div>

                {/* Template Status */}
                <div className="mt-6 p-4 bg-gray-900/30 border border-gray-700/30 rounded-lg">
                  <h3 className="text-sm font-semibold text-gray-300 mb-3">Template Status</h3>
                  <div className="p-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    {vendorcategories.map((category) => {
                      if ((isHardware || isSoftware) && (category.id === 'PD' || category.id === 'AVA')) return null;
                      if (isCertification && !['TV', 'MNT', 'PD', 'AVA'].includes(category.id)) return null;

                      const data = getDataForCategory(category.id);
                      const template = data.referenceTable || data.changeList;

                      return (
                        <div key={category.id} className="flex items-center gap-2">
                          <div className={`text-lg ${template ? 'text-green-400' : 'text-gray-300'}`}>
                            {template ? '✓' : '○'}
                          </div>
                          <div>
                            <div className="text-xs text-white font-semibold">{category.label}</div>
                            {template?.fileVersion && (
                              <div className="text-xs text-gray-500 font-mono">{template.fileVersion}</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════════
            TAB: PROJECT REPORTING UI LAYOUT
        ══════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'PROJECT_REPORTING_UI_LAYOUT' && (
          <div className="sm:mt-6 mt-1 space-y-6 ">
            {/* Selection List - Like other tabs */}
            <div className="w-full max-w-lg mx-auto lg:mx-0 lg:ml-110">
              <div className="bg-[#1a1d23] rounded-lg border border-gray-800 divide-y divide-gray-800">
                {ProjectReportingUILayout.map((item, index) => (
                  <div
                    key={index}
                    onClick={() => setSelectedPRItem(prev => (prev === item ? null : item))}
                    className={`px-4 py-3 cursor-pointer transition-colors ${selectedPRItem === item ? 'bg-gray-600 text-white' : 'hover:bg-gray-700'}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-white" />
                      <span className="text-base">{item}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Content appears below when Hardware is selected - Same pattern as other tabs */}
            {selectedPRItem === 'Hardware' && (
              <div className="w-full">
                <div className="bg-[#0F1217] border border-gray-800 rounded-xl overflow-hidden">
                  {/* Header */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between border-b border-gray-800 px-4 sm:px-6 py-4 bg-[#0A0C10] gap-2">
                    <div className="flex items-center flex-wrap gap-2 text-base sm:text-xl font-semibold text-white">
                      <span className="break-words">
                        Project Reporting UI Layout
                      </span>
                      <ChevronRight className="w-5 h-5 sm:w-7 sm:h-7 text-gray-400 flex-shrink-0" />
                      <span className="text-white break-words">
                        Hardware
                      </span>
                    </div>
                  </div>
                  {/* Edit Zone UI */}
                  <div className="bg-[#8C8C8C] px-4 sm:px-6 py-2 border-b border-gray-700">
                    <h2 className="text-black font-bold text-lg ">
                      Edit Zone
                    </h2>
                  </div>
                  {/* Main Content */}
                  <div className="p-4 sm:p-6 lg:p-8">
                    <div className="w-full">
                      <div className="mb-8">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-2">
                          <div className="text-sm font-medium text-white whitespace-nowrap">
                            Step 1 : Select a UI template
                          </div>
                          <div className="relative bg-white rounded-md">
                            <select
                              value={selectedTemplate}
                              onChange={(e) => {
                                setSelectedTemplate(e.target.value);
                              }}
                              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                              onBlur={() => setIsDropdownOpen(false)}
                              className="appearance-none bg-transparent text-black border border-gray-700 text-sm rounded-md px-4 py-2 pr-10 focus:outline-none focus:border-blue-500 w-full sm:w-auto"
                            >
                              <option value="default-template">Default template</option>
                              <option value="outsourcing-weekly-report">Outsourcing Weekly Report</option>
                              <option value="aic-weekly-highlights">AIC Weekly Highlights</option>
                            </select>
                            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                              {isDropdownOpen ? (
                                <ChevronUp className="w-4 h-4 text-black" />
                              ) : (
                                <ChevronDown className="w-4 h-4 text-black" />
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-sm font-medium text-white">
                          Step 2 : Customize the sections below based on the selected template
                        </div>
                      </div>
                      {/* Sections */}

                      <div className="space-y-8">
                        {sectionsOrder.map((sectionKey) => (
                          <React.Fragment key={sectionKey}>
                            {/* 1. Header */}
                            {sectionKey === "header" && (
                              <div>
                                <div className="flex items-center gap-3 mb-3">
                                  <h3 className="text-lg font-semibold text-white">#1. Header</h3>
                                </div>
                                <div className="bg-[#1A1F2A] border border-gray-700 rounded-xl p-6 text-center text-gray-400">
                                  Predefined by report type. Not editable.
                                </div>
                              </div>
                            )}

                            {/* 2. Highlight */}
                            {sectionKey === "highlight" && (
                              <div className="relative w-full">
                                {/* Header Section */}
                                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-2">
                                  {/* Left Side */}
                                  <div className="flex items-center gap-2 relative flex-wrap">
                                    <h3 className="text-sm sm:text-base font-semibold text-white">
                                      #2. Highlight
                                    </h3>
                                    {/* More Vertical Button */}
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setActiveSettingsButton(
                                          activeSettingsButton === 'highlight'
                                            ? null
                                            : 'highlight'
                                        );
                                      }}
                                      className="p-1 hover:bg-gray-700 rounded-lg transition-colors"
                                    >
                                      <MoreVertical className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                                    </button>

                                    {/* Settings Dropdown */}
                                    {activeSettingsButton === 'highlight' && (
                                      <div className="absolute top-8 left-0 sm:left-28 z-50 w-[180px] sm:w-[200px]">
                                        {/* Settings Button */}
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();

                                            setActiveSectionForSettings(
                                              activeSectionForSettings === 'highlight'
                                                ? null
                                                : 'highlight'
                                            );
                                          }}
                                          className="flex items-center justify-between w-full bg-white text-black border border-gray-400 px-3 py-2 text-xs sm:text-sm shadow-md rounded-t-md"
                                        >
                                          <span>Settings</span>
                                          {activeSectionForSettings === 'highlight' ? (
                                            <ChevronUp className="w-4 h-4" />
                                          ) : (
                                            <ChevronDown className="w-4 h-4" />
                                          )}
                                        </button>

                                        {/* Dropdown Options */}
                                        {activeSectionForSettings === 'highlight' && (
                                          <div className="w-full bg-white border border-gray-400 border-t-0 shadow-md rounded-b-md overflow-hidden">

                                            {[
                                              "Rename the section",
                                              "Disable the section",
                                              "Enable the section",
                                              "Add the same section"
                                            ].map((item, index) => (
                                              <button
                                                key={index}
                                                onClick={() => {
                                                  alert(item);
                                                  setActiveSectionForSettings(null);
                                                  setActiveSettingsButton(null);
                                                }}
                                                className="w-full text-left px-3 py-2 text-xs sm:text-sm hover:bg-blue-400 hover:text-white text-black"
                                              >
                                                {item}
                                              </button>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  {/* Add Widget Button */}
                                  <button
                                    onClick={() => {
                                      setSelectedSection("highlight");
                                      setShowAddWidgetModal(true);
                                    }}
                                    className="flex items-center justify-center gap-2 bg-violet-500 hover:bg-violet-600 border border-gray-700 px-3 py-2 rounded-lg text-xs sm:text-sm transition-colors w-full sm:w-auto"
                                  >
                                    <Plus className="w-4 h-4" />
                                    Add Widget
                                  </button>
                                </div>

                                {/* Widget Content */}
                                <div className="bg-[#1A1F2A] border border-gray-700 rounded-xl p-3 sm:p-6">
                                  <div className="bg-white border border-gray-700 rounded-xl p-2 sm:p-3">
                                    <div className="flex items-center justify-between gap-2 sm:gap-4">
                                      {/* Left Side Graph */}
                                      <div className="h-14 sm:h-16 bg-white rounded-lg flex items-end gap-0.5 p-1 w-[100px] sm:w-[140px]">
                                        <img
                                          src="/highlight.png"
                                          alt="Lamp"
                                          className="h-full w-full object-contain rounded-lg"
                                        />
                                      </div>
                                      {/* Center Count */}
                                      <div className="text-lg sm:text-2xl font-bold text-black whitespace-nowrap">
                                        TBD
                                      </div>
                                      {/* Right Side Icons */}
                                      <div className="flex items-center gap-1 sm:gap-2">
                                        <CircleArrowUp
                                          onClick={() => {
                                            if (
                                              sectionsOrder.indexOf("highlight") !== 0
                                            ) {
                                              moveSectionUp("highlight");
                                            }
                                          }}
                                          className={`w-5 h-5 sm:w-7 sm:h-7 ${sectionsOrder.indexOf("highlight") === 0
                                            ? "text-gray-400 cursor-not-allowed"
                                            : "text-blue-400 cursor-pointer hover:text-blue-700"
                                            }`}
                                        />
                                        <CircleArrowDown
                                          onClick={() => {
                                            if (
                                              sectionsOrder.indexOf("highlight") !==
                                              sectionsOrder.length - 1
                                            ) {
                                              moveSectionDown("highlight");
                                            }
                                          }}
                                          className={`w-5 h-5 sm:w-7 sm:h-7 ${sectionsOrder.indexOf("highlight") ===
                                            sectionsOrder.length - 1
                                            ? "text-gray-400 cursor-not-allowed"
                                            : "text-blue-400 cursor-pointer hover:text-blue-700"
                                            }`}
                                        />
                                        <CircleX className="w-5 h-5 sm:w-7 sm:h-7 text-blue-400 cursor-pointer hover:text-red-700" />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                            {/* ===================== 3. Chart ===================== */}
                            {sectionKey === "chart" && (
                              <div className="relative w-full">
                                {/* Header */}
                                <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-2">
                                  {/* Left Side */}
                                  <div className="flex items-center gap-2 relative flex-wrap">
                                    <h3 className="text-sm sm:text-base font-semibold text-white">
                                      #3. Chart
                                    </h3>
                                    {/* More Vertical Button */}
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setActiveSettingsButton(
                                          activeSettingsButton === 'chart'
                                            ? null
                                            : 'chart'
                                        );
                                      }}
                                      className="p-1 hover:bg-gray-700 rounded-lg transition-colors"
                                    >
                                      <MoreVertical className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                                    </button>
                                    {/* Settings Dropdown */}
                                    {activeSettingsButton === 'chart' && (
                                      <div className="absolute top-8 left-0 sm:left-24 z-50 w-[180px] sm:w-[200px]">
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setActiveSectionForSettings(
                                              activeSectionForSettings === 'chart'
                                                ? null
                                                : 'chart'
                                            );
                                          }}
                                          className="flex items-center justify-between w-full bg-white text-black border border-gray-400 px-3 py-2 text-xs sm:text-sm shadow-md rounded-t-md"
                                        >
                                          <span>Settings</span>
                                          {activeSectionForSettings === 'chart' ? (
                                            <ChevronUp className="w-4 h-4" />
                                          ) : (
                                            <ChevronDown className="w-4 h-4" />
                                          )}
                                        </button>
                                        {activeSectionForSettings === 'chart' && (
                                          <div className="w-full bg-white border border-gray-400 border-t-0 shadow-md rounded-b-md overflow-hidden">
                                            {[
                                              "Rename the section",
                                              "Disable the section",
                                              "Enable the section",
                                              "Add the same section"
                                            ].map((item, index) => (
                                              <button
                                                key={index}
                                                onClick={() => {
                                                  alert(item);
                                                  setActiveSectionForSettings(null);
                                                  setActiveSettingsButton(null);
                                                }}
                                                className="w-full text-left px-3 py-2 text-xs sm:text-sm hover:bg-blue-400 hover:text-white text-black"
                                              >
                                                {item}
                                              </button>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  {/* Add Widget Button */}
                                  <button
                                    onClick={() => {
                                      setSelectedSection("chart");
                                      setShowAddWidgetModal(true);
                                    }}
                                    className="flex items-center justify-center gap-2 bg-violet-500 hover:bg-violet-600 border border-gray-700 px-3 py-2 rounded-lg text-xs sm:text-sm transition-colors w-full sm:w-auto"
                                  >
                                    <Plus className="w-4 h-4" />
                                    Add Widget
                                  </button>
                                </div>
                                {/* Widget Content */}
                                <div className="bg-[#1A1F2A] border border-gray-700 rounded-xl p-3 sm:p-6">
                                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                                    {chartCards.map((card, index) => (
                                      <div
                                        key={card.id}
                                        className="bg-white border border-gray-700 rounded-xl p-2 sm:p-3"
                                      >
                                        <div className="flex items-center justify-between gap-2 sm:gap-4">
                                          {/* Image */}
                                          <div className="h-14 sm:h-16 bg-white rounded-lg flex items-end gap-0.5 p-1 w-[100px] sm:w-[140px]">
                                            <img
                                              src={card.image}
                                              alt="Chart"
                                              className="h-full w-full object-contain rounded-lg"
                                            />
                                          </div>
                                          {/* Widget Name */}
                                          <div className="text-sm sm:text-lg font-bold text-black whitespace-nowrap max-w-[180px] truncate">
                                            {
                                              card?.widget?.widgetName || ""
                                            }
                                          </div>
                                          {/* ================= ICONS ================= */}
                                          <div className="flex items-center gap-1 sm:gap-2">
                                            {/* ================= LEFT CARD ================= */}
                                            {index === 0 ? (
                                              <>
                                                {/* LEFT DISABLED */}
                                                <CircleArrowLeft
                                                  className="w-5 h-5 sm:w-7 sm:h-7 text-gray-300 cursor-not-allowed"
                                                />
                                                {/* RIGHT */}
                                                <CircleArrowRight
                                                  onClick={swapChartWidgets}
                                                  className="w-5 h-5 sm:w-7 sm:h-7 text-blue-400 cursor-pointer hover:text-blue-700"
                                                />
                                                {/* DELETE */}
                                                <CircleX
                                                  onClick={() => {
                                                    setChartCards((prev) => [
                                                      {
                                                        ...prev[0],
                                                        image: "",
                                                        widget: null
                                                      },
                                                      prev[1]
                                                    ]);
                                                  }}
                                                  className="w-5 h-5 sm:w-7 sm:h-7 text-red-400 cursor-pointer hover:text-red-700"
                                                />
                                              </>
                                            ) : (
                                              <>
                                                {/* LEFT */}
                                                <CircleArrowLeft
                                                  onClick={swapChartWidgets}
                                                  className="w-5 h-5 sm:w-7 sm:h-7 text-blue-400 cursor-pointer hover:text-blue-700"
                                                />
                                                {/* RIGHT DISABLED */}
                                                <CircleArrowRight
                                                  className="w-5 h-5 sm:w-7 sm:h-7 text-gray-300 cursor-not-allowed"
                                                />
                                                {/* DELETE */}
                                                <CircleX
                                                  onClick={() => {
                                                    setChartCards((prev) => [
                                                      prev[0],
                                                      {
                                                        ...prev[1],
                                                        image: "",
                                                        widget: null
                                                      }
                                                    ]);
                                                  }}
                                                  className="w-5 h-5 sm:w-7 sm:h-7 text-red-400 cursor-pointer hover:text-red-700"
                                                />
                                              </>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            )}
                            {/* ===================== 4. Table ===================== */}
                            {sectionKey === "table" && (
                              <div className="relative w-full">
                                {/* Header */}
                                <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-2">
                                  <div className="flex items-center gap-2 relative flex-wrap">
                                    <h3 className="text-sm sm:text-base font-semibold text-white">
                                      #4. Table
                                    </h3>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setActiveSettingsButton(
                                          activeSettingsButton === 'table'
                                            ? null
                                            : 'table'
                                        );
                                      }}
                                      className="p-1 hover:bg-gray-700 rounded-lg transition-colors"
                                    >
                                      <MoreVertical className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                                    </button>
                                    {/* Settings Dropdown */}
                                    {activeSettingsButton === 'table' && (
                                      <div className="absolute top-8 left-0 sm:left-24 z-50 w-[180px] sm:w-[200px]">
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setActiveSectionForSettings(
                                              activeSectionForSettings === 'table'
                                                ? null
                                                : 'table'
                                            );

                                          }}
                                          className="flex items-center justify-between w-full bg-white text-black border border-gray-400 px-3 py-2 text-xs sm:text-sm shadow-md rounded-t-md"
                                        >
                                          <span>Settings</span>
                                          {activeSectionForSettings === 'table' ? (
                                            <ChevronUp className="w-4 h-4" />
                                          ) : (
                                            <ChevronDown className="w-4 h-4" />
                                          )}
                                        </button>
                                        {activeSectionForSettings === 'table' && (
                                          <div className="w-full bg-white border border-gray-400 border-t-0 shadow-md rounded-b-md overflow-hidden">
                                            {[
                                              "Rename the section",
                                              "Disable the section",
                                              "Enable the section",
                                              "Add the same section"
                                            ].map((item, index) => (

                                              <button
                                                key={index}
                                                onClick={() => {

                                                  alert(item);
                                                  setActiveSectionForSettings(null);
                                                  setActiveSettingsButton(null);
                                                }}
                                                className="w-full text-left px-3 py-2 text-xs sm:text-sm hover:bg-blue-400 hover:text-white text-black"
                                              >
                                                {item}
                                              </button>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  {/* Add Widget Button */}
                                  <button
                                    onClick={() => {
                                      setSelectedSection("table");
                                      setShowAddWidgetModal(true);
                                    }}
                                    className="flex items-center justify-center gap-2 bg-violet-500 hover:bg-violet-600 border border-gray-700 px-3 py-2 rounded-lg text-xs sm:text-sm transition-colors w-full sm:w-auto"
                                  >
                                    <Plus className="w-4 h-4" />
                                    Add Widget
                                  </button>
                                </div>
                                {/* Widget Content */}

                                <div className="bg-[#1A1F2A] border border-gray-700 rounded-xl p-3 sm:p-6">
                                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                                    {tableCards.map((card, index) => (
                                      <div
                                        key={card.id}
                                        className="bg-white border border-gray-700 rounded-xl p-2 sm:p-3"
                                      >
                                        <div className="flex items-center justify-between gap-2 sm:gap-4">
                                          {/* Image */}
                                          <div className="h-14 sm:h-16 bg-white rounded-lg flex items-end gap-0.5 p-1 w-[100px] sm:w-[140px]">
                                            <img
                                              src={card.image}
                                              alt="Table"
                                              className="h-full w-full object-contain rounded-lg"
                                            />
                                          </div>
                                          {/* Widget Name */}
                                          <div className="text-sm sm:text-lg font-bold text-black whitespace-nowrap max-w-[180px] truncate">
                                            {
                                              card?.widget?.widgetName || ""
                                            }
                                          </div>
                                          {/* ================= ICONS ================= */}
                                          <div className="flex items-center gap-1 sm:gap-2">
                                            {/* ================= LEFT CARD ================= */}
                                            {index === 0 ? (
                                              <>
                                                <CircleArrowLeft
                                                  className="w-5 h-5 sm:w-7 sm:h-7 text-gray-300 cursor-not-allowed"
                                                />
                                                <CircleArrowRight
                                                  onClick={swapTableWidgets}
                                                  className="w-5 h-5 sm:w-7 sm:h-7 text-blue-400 cursor-pointer hover:text-blue-700"
                                                />
                                                <CircleX
                                                  onClick={() => {
                                                    setTableCards((prev) => [
                                                      {
                                                        ...prev[0],
                                                        image: "",
                                                        widget: null
                                                      },
                                                      prev[1]
                                                    ]);
                                                  }}
                                                  className="w-5 h-5 sm:w-7 sm:h-7 text-red-400 cursor-pointer hover:text-red-700"
                                                />
                                              </>
                                            ) : (
                                              <>
                                                <CircleArrowLeft
                                                  onClick={swapTableWidgets}
                                                  className="w-5 h-5 sm:w-7 sm:h-7 text-blue-400 cursor-pointer hover:text-blue-700"
                                                />
                                                <CircleArrowRight
                                                  className="w-5 h-5 sm:w-7 sm:h-7 text-gray-300 cursor-not-allowed"
                                                />
                                                <CircleX
                                                  onClick={() => {
                                                    setTableCards((prev) => [
                                                      prev[0],
                                                      {
                                                        ...prev[1],
                                                        image: "",
                                                        widget: null
                                                      }
                                                    ]);
                                                  }}
                                                  className="w-5 h-5 sm:w-7 sm:h-7 text-red-400 cursor-pointer hover:text-red-700"
                                                />
                                              </>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* 5. Mandatory Section */}
                            {sectionKey === "mandatory" && (
                              <div className="relative w-full">

                                {/* Header */}
                                <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-2">
                                  <div className="flex items-center gap-2 relative flex-wrap">
                                    <h3 className="text-sm sm:text-base font-semibold text-white">
                                      #5. Mandatory Section
                                    </h3>
                                    {/* More Vertical Button */}
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();

                                        setActiveSettingsButton(
                                          activeSettingsButton === 'mandatorysection'
                                            ? null
                                            : 'mandatorysection'
                                        );
                                      }}
                                      className="p-1 hover:bg-gray-700 rounded-lg transition-colors"
                                    >
                                      <MoreVertical className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                                    </button>

                                    {/* Settings Dropdown */}
                                    {activeSettingsButton === 'mandatorysection' && (
                                      <div className="absolute top-8 left-0 sm:left-40 z-50 w-[180px] sm:w-[200px]">

                                        {/* Settings Button */}
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();

                                            setActiveSectionForSettings(
                                              activeSectionForSettings === 'mandatorysection'
                                                ? null
                                                : 'mandatorysection'
                                            );
                                          }}
                                          className="flex items-center justify-between w-full bg-white text-black border border-gray-400 px-3 py-2 text-xs sm:text-sm shadow-md rounded-t-md"
                                        >
                                          <span>Settings</span>

                                          {activeSectionForSettings === 'mandatorysection' ? (
                                            <ChevronUp className="w-4 h-4" />
                                          ) : (
                                            <ChevronDown className="w-4 h-4" />
                                          )}
                                        </button>

                                        {/* Dropdown Options */}
                                        {activeSectionForSettings === 'mandatorysection' && (
                                          <div className="w-full bg-white border border-gray-400 border-t-0 shadow-md rounded-b-md overflow-hidden">

                                            {[
                                              "Rename the section",
                                              "Disable the section",
                                              "Enable the section",
                                              "Add the same section"
                                            ].map((item, index) => (
                                              <button
                                                key={index}
                                                onClick={() => {
                                                  alert(item);
                                                  setActiveSectionForSettings(null);
                                                  setActiveSettingsButton(null);
                                                }}
                                                className="w-full text-left px-3 py-2 text-xs sm:text-sm hover:bg-blue-400 hover:text-white text-black"
                                              >
                                                {item}
                                              </button>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>

                                  {/* Add Widget Button */}
                                  <button
                                    onClick={() => {
                                      setSelectedSection("mandatory");
                                      setShowAddWidgetModal(true);
                                    }}
                                    className="flex items-center justify-center gap-2 bg-violet-500 hover:bg-violet-600 border border-gray-700 px-3 py-2 rounded-lg text-xs sm:text-sm transition-colors w-full sm:w-auto"
                                  >
                                    <Plus className="w-4 h-4" />
                                    Add Widget
                                  </button>
                                </div>

                                {/* Single Card */}
                                <div className="bg-[#1A1F2A] border border-gray-700 rounded-xl p-3 sm:p-6">
                                  <div className="bg-white border border-gray-700 rounded-xl p-2 sm:p-3">
                                    <div className="flex items-center justify-between gap-2 sm:gap-4">
                                      {/* Left Side Graph */}
                                      <div className="h-14 sm:h-16 bg-white rounded-lg flex items-end gap-0.5 p-1 w-[100px] sm:w-[140px]">
                                        <img
                                          src="/highlight.png"
                                          alt="Lamp"
                                          className="h-full w-full object-contain rounded-lg"
                                        />
                                      </div>
                                      {/* Center Count */}
                                      <div className="text-lg sm:text-2xl font-bold text-black whitespace-nowrap">
                                        TBD
                                      </div>

                                      {/* Right Side Cancel Icon */}
                                      <div className="flex items-center">
                                        <CircleX className="w-5 h-5 sm:w-7 sm:h-7 text-blue-400 cursor-pointer hover:text-red-700" />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  </div>
                  {/* Footer Buttons */}
                  <div className="border-t border-gray-800 bg-[#0A0C10] px-4 sm:px-6 py-5">
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-6 lg:gap-10 w-full">

                      <button className="flex items-center justify-center gap-2 px-4 sm:px-6 py-3 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm sm:text-base w-full sm:w-auto transition-colors">
                        <Eye className="w-4 h-4 sm:w-5 sm:h-5" />
                        <span>Preview</span>
                      </button>

                      {/* Save as Draft */}
                      <button
                        onClick={() => handleSubmitLayout('draft')}
                        className="flex items-center justify-center gap-2 px-4 sm:px-6 py-3 bg-blue-600 hover:bg-blue-500 rounded-lg text-white text-sm sm:text-base w-full sm:w-auto transition-colors"
                      >
                        <Save className="w-4 h-4 sm:w-5 sm:h-5" />
                        <span>Save as Draft</span>
                      </button>

                      {/* Publish */}
                      <button
                        onClick={() => handleSubmitLayout('published')}
                        className="flex items-center justify-center gap-2 px-4 sm:px-6 py-3 bg-green-600 hover:bg-green-500 rounded-lg text-white font-medium text-sm sm:text-base w-full sm:w-auto transition-colors"
                      >
                        <Upload className="w-4 h-4 sm:w-5 sm:h-5" />
                        <span>Publish</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {showAddWidgetModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="bg-[#183153] border border-gray-600 rounded-2xl w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col shadow-2xl">
            {/* ================= HEADER ================= */}
            <div className="px-6 py-4 border-b border-gray-600 flex items-center justify-between bg-[#183153]">
              <h2 className="text-2xl font-semibold text-white">
                Add Widget
              </h2>

              <button
                onClick={() => setShowAddWidgetModal(false)}
                className="text-gray-400 hover:text-white p-2"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            {/* ================= TABS ================= */}
            <div className="flex bg-[#183153] gap-2 sm:pt-7 pt-3 mb-2">

              <button
                onClick={() => setActiveWidgetTab('Chart')}
                className={`flex-1 py-2 text-lg font-medium transition-all rounded-full ${activeWidgetTab === 'Chart'
                  ? 'bg-stone-700 text-white'
                  : 'text-black hover:text-white bg-gray-400'
                  }`}
              >
                Chart
              </button>

              <button
                onClick={() => setActiveWidgetTab('Table and Matrix')}
                className={`flex-1 py-2 text-lg font-medium transition-all rounded-full ${activeWidgetTab === 'Table and Matrix'
                  ? 'bg-stone-700 text-white'
                  : 'text-black hover:text-white bg-gray-400'
                  }`}
              >
                Table and Matrix
              </button>
            </div>
            {/* ================= MAIN CONTENT ================= */}
            <div className="flex flex-1 overflow-hidden bg-white rounded-3xl border border-gray-200">
              <div className="w-5/12 p-5 overflow-auto">
                {/* ================= CHART ================= */}
                {activeWidgetTab === 'Chart' ? (
                  <div className="space-y-1">
                    {chartWidgets.map((widget, i) => {
                      const selectedChartWidgets =
                        selectedWidgets[selectedSection]?.filter(
                          (item) =>
                            chartWidgets.some(
                              (chart) => chart.widgetKey === item.widgetKey
                            )
                        ) || [];

                      const isChartLimitReached =
                        selectedChartWidgets.length >= 2;

                      const isChecked =
                        selectedWidgets[selectedSection]?.some(
                          (item) => item.widgetKey === widget.widgetKey
                        ) || false;

                      return (

                        <label
                          key={`chart-${widget.widgetKey}-${i}`}
                          className={`flex items-center gap-3 p-4 rounded-xl border transition-all
                ${isChartLimitReached && !isChecked
                              ? "opacity-50 cursor-not-allowed bg-gray-100 border-gray-200"
                              : "hover:bg-gray-50 cursor-pointer border-transparent hover:border-gray-200"
                            }`}
                        >
                          <input
                            type="checkbox"

                            className={`min-w-[20px] min-h-[20px] flex-shrink-0 accent-black border-[3px] border-black
                  ${isChartLimitReached && !isChecked
                                ? "cursor-not-allowed"
                                : "cursor-pointer"
                              }`
                            }
                            checked={isChecked}
                            disabled={
                              isChartLimitReached && !isChecked
                            }

                            onChange={(e) => {

                              const selectedChartWidgets =
                                selectedWidgets[selectedSection]?.filter(
                                  (item) =>
                                    chartWidgets.some(
                                      (chart) =>
                                        chart.widgetKey === item.widgetKey
                                    )
                                ) || [];

                              if (e.target.checked) {

                                if (selectedChartWidgets.length >= 2) {

                                  setChartError(
                                    "More than two chart widget selections are not allowed"
                                  );

                                  return;
                                }
                                setChartError("");
                                setSelectedWidgets((prev) => ({
                                  ...prev,
                                  [selectedSection]: [
                                    ...prev[selectedSection].filter(
                                      (item) =>
                                        item.widgetKey !== widget.widgetKey
                                    ),

                                    {
                                      widgetKey: widget.widgetKey,
                                      widgetName: widget.widgetName
                                    }
                                  ]
                                }));

                              } else {
                                setChartError("");
                                setSelectedWidgets((prev) => ({
                                  ...prev,
                                  [selectedSection]:
                                    prev[selectedSection].filter(
                                      (item) =>
                                        item.widgetKey !== widget.widgetKey
                                    )
                                }));
                              }
                            }}
                          />
                          <span className="text-black font-bold text-sm">
                            .
                          </span>

                          <span className="text-black text-[16px] font-semibold">
                            {widget.widgetName}
                          </span>

                        </label>
                      );
                    })}
                    {/* ================= CHART ERROR ================= */}
                    {chartError && (
                      <p className="text-red-500 text-sm font-medium mt-2">
                        {chartError}
                      </p>
                    )}
                  </div>
                ) : (

                  /* ================= TABLE ================= */
                  <div className="space-y-1">
                    {tableWidgets.map((widget, i) => {

                      const selectedTableWidgets =
                        selectedWidgets[selectedSection]?.filter(
                          (item) =>
                            tableWidgets.some(
                              (table) => table.widgetKey === item.widgetKey
                            )
                        ) || [];

                      const isTableLimitReached =
                        selectedTableWidgets.length >= 2;

                      const isChecked =
                        selectedWidgets[selectedSection]?.some(
                          (item) => item.widgetKey === widget.widgetKey
                        ) || false;

                      return (
                        <label
                          key={`table-${widget.widgetKey}-${i}`}
                          className={`flex items-center gap-3 p-4 rounded-xl border transition-all
                ${isTableLimitReached && !isChecked
                              ? "opacity-50 cursor-not-allowed bg-gray-100 border-gray-200"
                              : "hover:bg-gray-50 cursor-pointer border-transparent hover:border-gray-200"
                            }`}
                        >
                          <input
                            type="checkbox"

                            className={`min-w-[20px] min-h-[20px] flex-shrink-0 accent-black border-[3px] border-black
                  ${isTableLimitReached && !isChecked
                                ? "cursor-not-allowed"
                                : "cursor-pointer"
                              }`
                            }
                            checked={isChecked}
                            disabled={
                              isTableLimitReached && !isChecked
                            }
                            onChange={(e) => {
                              const selectedTableWidgets =
                                selectedWidgets[selectedSection]?.filter(
                                  (item) =>
                                    tableWidgets.some(
                                      (table) =>
                                        table.widgetKey === item.widgetKey
                                    )
                                ) || [];

                              if (e.target.checked) {
                                if (selectedTableWidgets.length >= 2) {
                                  setTableError(
                                    "More than two table widget selections are not allowed"
                                  );
                                  return;
                                }
                                setTableError("");

                                setSelectedWidgets((prev) => ({
                                  ...prev,
                                  [selectedSection]: [

                                    ...prev[selectedSection].filter(
                                      (item) =>
                                        item.widgetKey !== widget.widgetKey
                                    ),
                                    {
                                      widgetKey: widget.widgetKey,
                                      widgetName: widget.widgetName
                                    }
                                  ]
                                }));

                              } else {
                                setTableError("");
                                setSelectedWidgets((prev) => ({
                                  ...prev,
                                  [selectedSection]:
                                    prev[selectedSection].filter(
                                      (item) =>
                                        item.widgetKey !== widget.widgetKey
                                    )
                                }));
                              }
                            }}
                          />
                          <span className="text-black font-bold text-sm">
                            .
                          </span>
                          <span className="text-black text-[16px] font-semibold">
                            {widget.widgetName}
                          </span>
                        </label>
                      );
                    })}
                    {/* ================= TABLE ERROR ================= */}
                    {tableError && (
                      <p className="text-red-500 text-sm font-medium mt-2">
                        More than two table widget selections are not allowed
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* ================= RIGHT SIDE ================= */}
              <div className="flex-1 p-6 overflow-auto bg-white">
                <div className="border border-gray-200 rounded-2xl p-3 h-full">
                  <h3 className="font-bold text-xl mb-5 text-gray-900 underline">
                    Widget guide
                  </h3>
                  {activeWidgetTab === 'Chart' ? (
                    <div className="space-y-1 text-[15px] text-black">
                      <p>
                        <span>Allowed section:</span> Chart
                      </p>
                      <p>
                        <span>Multi-select:</span> Yes (up to 2 widgets per section)
                      </p>
                      <p>
                        <span>Data source:</span> UrTracker DB
                      </p>
                      <p>
                        <span>Output:</span> Chart
                      </p>
                      <p>
                        <span>Usage:</span> Visualizes key metrics and trends
                        derived from table data (e.g., open PR count, status
                        distribution, trend over time).
                      </p>
                      <p>
                        <span>Notes/Limit:</span> Charts are read-only and follow
                        the selected report filters (date/project/category).
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-1 text-[15px] text-black">
                      <p>
                        <span>Allowed section:</span> Table (and Mandatory section if required)
                      </p>
                      <p>
                        <span>Multi-select:</span> Yes (up to 2 widgets per section)
                      </p>
                      <p>
                        <span>Data source:</span> UrTracker DB
                      </p>
                      <p>
                        <span>Output:</span> Table / Matrix
                      </p>
                      <p>
                        <span>Usage:</span> Presents detailed records or grouped
                        summaries based on UrTracker fields (e.g., severity × state matrix,
                        severity × assignee matrix).
                      </p>
                      <p>
                        <span>Notes/Limit:</span> Some tables may be mandatory for
                        specific report types and cannot be removed.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {/* ================= FOOTER ================= */}
            <div className="p-4 bg-[#183153] flex justify-end gap-3">
              <button
                onClick={() => setShowAddWidgetModal(false)}
                className="px-8 py-2 text-white bg-gray-500 hover:bg-gray-600 rounded-lg font-medium"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  console.log("Selected Widgets:", selectedWidgets);
                  setShowAddWidgetModal(false);
                }}
                className="px-8 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium"
              >
                Add Selected
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default TemplateManagement;

